import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery, Tooltip } from "@material-ui/core";
import { useThemeContext } from "../../context/DarkMode";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import InternalChatPanel from "./InternalChatPanel";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import TicketsTagSelect from "../TicketsTagSelect";
import TicketsUserSelect from "../TicketsUserSelect";
import TicketsContactTypeSelect from "../TicketsContactTypeSelect";
import { Button, Select, MenuItem, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography } from "@material-ui/core";
import PeopleIcon from "@material-ui/icons/People";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FilterListIcon from "@material-ui/icons/FilterList";
import { getSocket } from "../../services/socket-io";
import api from "../../services/api";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { useUiPrefs } from "../../context/UiPrefs";
import BatchActionBar from "../BatchActionBar";
import BatchTransferDialog from "../BatchTransferDialog";
import BatchConfirmDeleteDialog from "../BatchConfirmDeleteDialog";
import ResolveTicketDialog from "../ResolveTicketDialog";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.background.paper,
  },
  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
  },
  tab: {
    minWidth: 80,
    width: 80,
    fontSize: 11,
    minHeight: 52,
    // Tab activo más destacado: fondo azul claro + texto bold (además de la línea azul).
    "&.Mui-selected": {
      backgroundColor: "rgba(25, 118, 210, 0.08)",
      fontWeight: 700,
    },
    // feat_142: en <1280px (compact) los tabs muestran solo ícono → más angostos/bajos.
    [theme.breakpoints.down("md")]: {
      minWidth: 48,
      width: 48,
      minHeight: 44,
    },
  },
  ticketOptionsBox: {
    display: "flex",
    alignItems: "center",
    // feat_326 fix: los filtros que no entran BAJAN a una segunda fila en vez de
    // desbordar. Antes el único recurso era encoger (los wrappers `isCompact`
    // con flexShrink), y con los dos controles nuevos del modo smart eso ya no
    // alcanzaba en ~1024px. `rowGap` separa las filas cuando wrappea.
    flexWrap: "wrap",
    gap: theme.spacing(1),
    rowGap: 4,
    // La fila crece en alto al wrappear: sin flexShrink:0 el contenedor column
    // padre podría comprimirla y cortar la segunda fila.
    flexShrink: 0,
    background: theme.palette.background.paper,
    padding: "4px 8px",
    // feat_142: gaps/padding reducidos en compact para que entren todos los filtros.
    [theme.breakpoints.down("md")]: {
      padding: "2px 4px",
      gap: 3,
      rowGap: 3,
    },
  },
  filtersLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  serachInputWrapper: {
    flex: 1,
    background: theme.palette.background.default,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
  },
  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },
  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
    color: theme.palette.text.primary, 
    backgroundColor: theme.palette.background.default,
  },
  badge: {
    right: "-10px",
  },
  pulsing: {
    animation: "$pulse 1.5s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.45 },
    "100%": { opacity: 1 },
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },
}));


// feat_323 — statuses de la bandeja unificada. Constante A NIVEL MÓDULO a
// propósito: un `status={["open","pending"]}` inline sería un array nuevo en
// cada render, y aunque los effects de TicketsList se protegen con statusKey,
// la prop en sí seguiría cambiando de identidad en cada pasada.
const UNIFIED_STATUSES = ["open", "pending"];

// feat_325 — bandeja smart. Las DOS listas tienen que PARTICIONAR el conjunto
// activo, no recortarlo: EN ESPERA se lleva los pending sin cola y EN ATENCIÓN
// se lleva TODO lo demás (incluidos los `open` sin cola, que con un "assigned"
// a secas no caerían en ninguna de las dos y desaparecerían del inbox).
const SMART_ATTENDING_STATUSES = ["open", "pending"];
const SMART_WAITING_STATUSES = ["pending"];

const buildFilterKey = (companyId, userId) =>
  `waia:ticketsFilter:${companyId}:${userId}`;

const loadFilterState = (companyId, userId, userQueues) => {
  try {
    const raw = localStorage.getItem(buildFilterKey(companyId, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const validIds = (userQueues || []).map(q => q.id);
    const filteredQueues = (parsed.selectedQueueIds || []).filter(id => validIds.includes(id));
    const filteredPrev = parsed.prevSelectedQueueIds
      ? parsed.prevSelectedQueueIds.filter(id => validIds.includes(id))
      : null;
    return {
      selectedQueueIds: filteredQueues,
      showAllTickets: !!parsed.showAllTickets,
      showOrphans: parsed.showOrphans !== false,
      prevShowOrphans: parsed.prevShowOrphans ?? null,
      prevSelectedQueueIds: filteredPrev,
      selectedTagIds: Array.isArray(parsed.selectedTagIds) ? parsed.selectedTagIds : [],
      selectedContactTypeIds: Array.isArray(parsed.selectedContactTypeIds) ? parsed.selectedContactTypeIds : [],
      // feat_301 — filtro por usuario asignado
      filterUserIds: Array.isArray(parsed.filterUserIds)
        ? parsed.filterUserIds.map(Number).filter(n => Number.isInteger(n) && n > 0)
        : null,
      includeUnassigned: !!parsed.includeUnassigned,
      // feat_344 — estado previo al tildar "Todos" en el filtro de usuarios
      // (simétrico a prevSelectedQueueIds/prevShowOrphans del de colas).
      prevFilterUserIds: Array.isArray(parsed.prevFilterUserIds)
        ? parsed.prevFilterUserIds.map(Number).filter(n => Number.isInteger(n) && n > 0)
        : null,
      prevIncludeUnassigned: typeof parsed.prevIncludeUnassigned === "boolean"
        ? parsed.prevIncludeUnassigned
        : null,
      // feat_326 — bandeja smart: orden + filtro "sin responder". El orden se
      // valida contra la lista cerrada: un valor viejo o corrupto en el
      // localStorage cae a "chrono" y no puede llegar al backend.
      sortOrder: ["chrono", "urgent"].includes(parsed.sortOrder)
        ? parsed.sortOrder
        : "chrono",
      onlyUnanswered: !!parsed.onlyUnanswered,
    };
  } catch {
    return null;
  }
};

const saveFilterState = (companyId, userId, state) => {
  try {
    localStorage.setItem(buildFilterKey(companyId, userId), JSON.stringify(state));
  } catch {
    // silently ignore quota or private-mode errors
  }
};

const TicketsManager = ({ onSelectInternalUser, ticketId, internalChatUserRef }) => {
  const classes = useStyles();
  const theme = useTheme();
  // feat_142: <1280px → modo compacto (tabs solo ícono, filtros angostos, gaps reducidos).
  // feat_328: la densidad "compact" activa el modo compacto también en
  // pantallas grandes (el viewport solo ya no alcanza como criterio).
  const { density } = useThemeContext();
  const isCompact =
    useMediaQuery(theme.breakpoints.down("md")) || density === "compact";
  const history = useHistory();
  const [searchParam, setSearchParam] = useState("");
  const debouncedSearchParam = useDebouncedValue(searchParam, 300);
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [newTicketInitialContact, setNewTicketInitialContact] = useState(null);

  const handleStartChatWithContact = useCallback((contact) => {
    setNewTicketInitialContact(contact);
    setNewTicketModalOpen(true);
  }, []);

  const handleCloseNewTicketModal = useCallback(() => {
    setNewTicketModalOpen(false);
    setNewTicketInitialContact(null);
  }, []);
  // Grupos/Canales viven en UiPrefsContext (toggles en el menú de perfil del header).
  const { showGroups, hideChannels } = useUiPrefs();
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [internalUnread, setInternalUnread] = useState(0);
  const userQueueIds = (user.queues || []).map((q) => q.id);

  // Load persisted filter state for this user — fallback to defaults if none saved
  const persisted = useMemo(
    () => loadFilterState(user.companyId, user.id, user.queues),
    // se lee una sola vez al montar; los cambios posteriores se propagan por los setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  // Con estado persistido se respeta tal cual (aunque sea false); sin él,
  // "Todos" arranca ON solo para admin (primera visita).
  const [showAllTickets, setShowAllTickets] = useState(
    persisted
      ? !!persisted.showAllTickets
      : user.profile?.toUpperCase() === "ADMIN"
  );

  const [selectedQueueIds, setSelectedQueueIds] = useState(
    persisted?.selectedQueueIds ?? userQueueIds
  );
  const [showOrphans, setShowOrphans] = useState(
    persisted?.showOrphans ?? true
  );
  // prevShowOrphans / prevSelectedQueueIds store values before "Todos" ON to restore on toggle-off
  const [prevShowOrphans, setPrevShowOrphans] = useState(
    persisted?.prevShowOrphans ?? null
  );
  const [prevSelectedQueueIds, setPrevSelectedQueueIds] = useState(
    persisted?.prevSelectedQueueIds ?? null
  );

  const [contactStateFilter, setContactStateFilter] = useState("");
  const [availableStates, setAvailableStates] = useState([]);

  // feat_326 — solo se usan en modo smart (el gate isSmartInbox), pero el
  // estado vive acá y se persiste igual que el resto de los filtros.
  const [sortOrder, setSortOrder] = useState(
    persisted?.sortOrder ?? "chrono"
  );
  const [onlyUnanswered, setOnlyUnanswered] = useState(
    persisted?.onlyUnanswered ?? false
  );

  // ── feat_113: selección múltiple + soft-delete ─────────────────────────────
  const isAdmin = user?.profile === "admin";
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const visibleIdsRef = useRef([]);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [batchTransferOpen, setBatchTransferOpen] = useState(false);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [deletedRefreshKey, setDeletedRefreshKey] = useState(0);
  const [hardDeleteConfirmOpen, setHardDeleteConfirmOpen] = useState(false);
  const [hardDeleteSingleId, setHardDeleteSingleId] = useState(null);

  const exitSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const enterSelection = useCallback((id) => {
    setSelectionMode(true);
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectionMode(true);
    setSelectedIds(new Set(visibleIdsRef.current || []));
  }, []);

  const handleVisibleTicketsChange = useCallback((ids) => {
    visibleIdsRef.current = ids;
  }, []);

  // Reset selection cuando cambia el tab o el sub-tab (mejora UX, evita selección "fantasma")
  useEffect(() => {
    if (selectionMode) exitSelection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, tabOpen]);

  const showToastResult = (processed, skipped, kind) => {
    const total = (processed?.length || 0) + (skipped?.length || 0);
    if ((skipped?.length || 0) === 0) {
      toast.success(i18n.t(`batchSelection.toast.${kind}Ok`, { count: processed.length }));
    } else {
      toast.info(i18n.t(`batchSelection.toast.${kind}Partial`, {
        processed: processed.length,
        skipped: skipped.length,
        total,
      }));
    }
  };

  const handleBatchMarkRead = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchLoading(true);
    try {
      const { data } = await api.post("/tickets/batch/read", { ticketIds: ids });
      showToastResult(data.processed, data.skipped, "markRead");
      exitSelection();
    } catch (err) {
      toastError(err);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchResolve = async (type) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchLoading(true);
    try {
      const { data } = await api.post("/tickets/batch/resolve", {
        ticketIds: ids,
        resolution: { type },
      });
      showToastResult(data.processed, data.skipped, "resolve");
      setResolveDialogOpen(false);
      exitSelection();
    } catch (err) {
      toastError(err);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchTransfer = async (queueId) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchLoading(true);
    try {
      const { data } = await api.post("/tickets/batch/transfer", {
        ticketIds: ids,
        queueId,
      });
      showToastResult(data.processed, data.skipped, "transfer");
      setBatchTransferOpen(false);
      exitSelection();
    } catch (err) {
      toastError(err);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchLoading(true);
    try {
      const { data } = await api.post("/tickets/batch/delete", { ticketIds: ids });
      setBatchDeleteOpen(false);
      exitSelection();
      // Toast con DESHACER (5s)
      toast(
        ({ closeToast }) => (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span>{i18n.t("batchSelection.toast.deletedWithUndo", { count: data.processed.length })}</span>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={async () => {
                try {
                  await api.post("/tickets/batch/restore", { ticketIds: data.processed });
                  toast.success(i18n.t("batchSelection.toast.undoOk"));
                  setDeletedRefreshKey(k => k + 1);
                } catch (e) {
                  toastError(e);
                }
                closeToast();
              }}
            >
              {i18n.t("batchSelection.toast.undo")}
            </Button>
          </div>
        ),
        { autoClose: 5000 }
      );
      setDeletedRefreshKey(k => k + 1);
    } catch (err) {
      toastError(err);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchRestore = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchLoading(true);
    try {
      const { data } = await api.post("/tickets/batch/restore", { ticketIds: ids });
      showToastResult(data.processed, data.skipped, "restore");
      exitSelection();
      setDeletedRefreshKey(k => k + 1);
    } catch (err) {
      toastError(err);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleRestoreSingle = async (ticketId) => {
    try {
      await api.post("/tickets/batch/restore", { ticketIds: [ticketId] });
      toast.success(i18n.t("batchSelection.toast.restoreOk", { count: 1 }));
      setDeletedRefreshKey(k => k + 1);
    } catch (err) {
      toastError(err);
    }
  };

  const handleHardDeleteSingle = (ticketId) => {
    setHardDeleteSingleId(ticketId);
    setHardDeleteConfirmOpen(true);
  };

  const handleBatchHardDelete = () => {
    setHardDeleteSingleId(null);
    setHardDeleteConfirmOpen(true);
  };

  const confirmHardDelete = async () => {
    setHardDeleteConfirmOpen(false);
    const ids = hardDeleteSingleId !== null ? [hardDeleteSingleId] : Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchLoading(true);
    try {
      const { data } = await api.post("/tickets/batch/hard-delete", { ticketIds: ids });
      const processed = data.processed?.length ?? 0;
      const skipped = data.skipped?.length ?? 0;
      const total = processed + skipped;
      if (skipped === 0) {
        toast.success(i18n.t("batchSelection.toast.hardDeleteOk", { count: processed }));
      } else {
        toast.warn(i18n.t("batchSelection.toast.hardDeletePartial", { processed, total, skipped }));
      }
      if (hardDeleteSingleId === null) exitSelection();
      setHardDeleteSingleId(null);
      setDeletedRefreshKey(k => k + 1);
    } catch (err) {
      toastError(err);
    } finally {
      setBatchLoading(false);
    }
  };

  const isDeletedTab = tab === "deleted";

  const [selectedTagIds, setSelectedTagIds] = useState(
    persisted?.selectedTagIds ?? []
  );
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedContactTypeIds, setSelectedContactTypeIds] = useState(
    persisted?.selectedContactTypeIds ?? []
  );
  const [availableContactTypes, setAvailableContactTypes] = useState([]);

  // ── feat_301: filtro por usuario asignado (solo admins + setting ON) ────────
  // El setting viene del SettingsContext (fetch inicial + socket "settings" ya
  // resueltos ahí) → no hace falta un GET /settings propio ni otro listener.
  const { getSetting } = useContext(SettingsContext);
  const showUserFilter = getSetting("showUserFilter") === "true";
  // ── feat_323: modo de bandeja ──────────────────────────────────────────────
  // "split" (default, y lo que devuelve getSetting cuando la key no existe) =
  // comportamiento histórico con los dos sub-tabs. "unified" = UNA lista con
  // open+pending mezclados por updatedAt. La key es pública (publicKeys del
  // SettingController) porque la consumen todos los perfiles, no solo el admin.
  // feat_325 suma "smart": vuelven los dos sub-tabs, pero el corte ya no es
  // open vs pending sino TIENE DESTINO vs HUÉRFANO.
  const inboxMode = getSetting("inboxMode") || "split";
  const isUnifiedInbox = inboxMode === "unified";
  const isSmartInbox = inboxMode === "smart";
  // En unificado los dos badges salen de la MISMA lista (2º arg de updateCount);
  // en split siguen saliendo de openCount/pendingCount, uno por lista.
  const [unifiedOpenCount, setUnifiedOpenCount] = useState(0);
  const [unifiedPendingCount, setUnifiedPendingCount] = useState(0);
  // En smart el badge azul cuenta TODA la bandeja de atención (open + pending
  // con destino) y el ámbar los huérfanos, espejando los dos sub-tabs.
  const smartAttendingCount = unifiedOpenCount + unifiedPendingCount;
  const displayOpenCount = isSmartInbox
    ? smartAttendingCount
    : isUnifiedInbox
    ? unifiedOpenCount
    : openCount;
  const displayPendingCount = isUnifiedInbox ? unifiedPendingCount : pendingCount;
  // feat_326 — INVARIANTE: con "solo sin responder" activo los badges muestran
  // el conteo FILTRADO (los counts vienen de las mismas listas recortadas). El
  // ícono al lado del badge es lo que evita leer un número bajo como "no hay
  // trabajo" cuando en realidad hay tickets escondidos por el filtro.
  const unansweredFilterIcon = isSmartInbox && onlyUnanswered ? (
    <Tooltip title={i18n.t("settingsExtra.inboxUnansweredActive")} arrow>
      <FilterListIcon style={{ fontSize: 12, color: "#e65100", marginLeft: 2 }} />
    </Tooltip>
  ) : null;
  // Tooltips de los badges: en smart explican el corte por destino.
  const openBadgeTooltip = isSmartInbox
    ? "settingsExtra.inboxSmartAttendingTooltip"
    : "settingsExtra.inboxOpenTooltip";
  const pendingBadgeTooltip = isSmartInbox
    ? "settingsExtra.inboxSmartWaitingTooltip"
    : "settingsExtra.inboxPendingTooltip";
  const [filterUserIds, setFilterUserIds] = useState(
    persisted?.filterUserIds && (persisted.filterUserIds.length > 0 || persisted.includeUnassigned)
      ? persisted.filterUserIds
      : [user.id]
  );
  const [includeUnassigned, setIncludeUnassigned] = useState(
    persisted?.includeUnassigned ?? false
  );
  // feat_344 — memoria para restaurar al destildar "Todos" (filtro de usuarios).
  const [prevFilterUserIds, setPrevFilterUserIds] = useState(
    persisted?.prevFilterUserIds ?? null
  );
  const [prevIncludeUnassigned, setPrevIncludeUnassigned] = useState(
    persisted?.prevIncludeUnassigned ?? null
  );
  const [availableUsers, setAvailableUsers] = useState([]);
  // Con el filtro oculto (no-admin, setting OFF o "Ver como usuario" activo)
  // NO se manda nada al backend — el listado se comporta como antes del feature.
  const userFilterActive = isAdmin && showUserFilter;
  const effectiveFilterUserIds = userFilterActive ? filterUserIds : [];
  const effectiveIncludeUnassigned = userFilterActive ? includeUnassigned : false;

  // Persist filter changes to localStorage
  useEffect(() => {
    saveFilterState(user.companyId, user.id, {
      selectedQueueIds, showAllTickets, showOrphans,
      prevShowOrphans, prevSelectedQueueIds, selectedTagIds, selectedContactTypeIds,
      filterUserIds, includeUnassigned,
      prevFilterUserIds, prevIncludeUnassigned, // feat_344
      // feat_326 — van también a las DEPS de abajo: sin eso el valor solo se
      // persistiría cuando cambia OTRO filtro.
      sortOrder, onlyUnanswered,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQueueIds, showAllTickets, showOrphans, prevShowOrphans, prevSelectedQueueIds, selectedTagIds, selectedContactTypeIds, filterUserIds, includeUnassigned, prevFilterUserIds, prevIncludeUnassigned, sortOrder, onlyUnanswered, user.companyId, user.id]);

  useEffect(() => {
    api.get("/contact-states").then(r => setAvailableStates(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/tags").then(r => setAvailableTags(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/contact-types").then(r => setAvailableContactTypes(r.data || [])).catch(() => {});
  }, []);

  // feat_301: usuarios de la empresa para el filtro (solo admin). /users pagina
  // de a 20 → juntamos páginas con cap defensivo, mismo molde que ViewAsUserModal.
  useEffect(() => {
    if (!isAdmin || !showUserFilter) return undefined;
    let cancelled = false;
    const loadAllUsers = async () => {
      const MAX_PAGES = 10;
      let pageNumber = 1;
      let collected = [];
      let hasMore = true;
      while (hasMore && pageNumber <= MAX_PAGES) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get("/users", { params: { pageNumber } });
        collected = [...collected, ...(data.users || [])];
        hasMore = !!data.hasMore;
        pageNumber += 1;
      }
      if (cancelled) return;
      setAvailableUsers(collected.filter(u => !u.isSuperAdmin));
    };
    loadAllUsers().catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin, showUserFilter]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
      setSearchParam("");
    }
  }, [tab]);

  // feat_292: seed the global internal-unread badge from DB (sum of per-sender
  // unread counts) on mount, instead of a naive in-memory counter. The +1 on
  // socket + reset-to-0 on entering the interno tab (below) still apply.
  useEffect(() => {
    if (!user?.id) return;
    api
      .get("/internal-messages/unread-counts")
      .then(({ data }) => {
        const total = (data || []).reduce((acc, c) => acc + (c.unread || 0), 0);
        setInternalUnread(total);
      })
      .catch(() => {});
  }, [user?.id]);

  // Track unread internal messages when not on interno tab
  useEffect(() => {
    const socket = getSocket();
    const handleInternalMessage = (data) => {
      const msg = data.message || data;
      if (msg.toUserId === user?.id && tab !== "interno") {
        setInternalUnread((prev) => prev + 1);
      }
    };
    socket.on("internalMessage", handleInternalMessage);
    // SINGLETON: off() selectivo, NUNCA disconnect() (mataría toda la app).
    return () => { socket.off("internalMessage", handleInternalMessage); };
  }, [user?.id, tab]);

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    setSearchParam(searchedTerm);
    if (searchedTerm === "") {
      setTab("open");
    }
  };

  // Derive the logical key for the currently active tab/sub-tab.
  // Used as the localStorage suffix for last-ticket memory.
  // tab=="open" splits into tabOpen=="open" (EN ATENCIÓN) or "pending" (EN ESPERA).
  const activeTabKey = () => (tab === "open" ? tabOpen : tab);

  // Callback passed down to the pending TicketsList → TicketListItem.
  // Called after a ticket is accepted so the left panel switches to EN ATENCIÓN.
  const handleAcceptTicket = useCallback(() => {
    setTabOpen("open");
  }, []);

  const handleToggleShowAll = () => {
    setShowAllTickets(prev => {
      const next = !prev;
      if (next) {
        // Turning Todos ON: memorize current state and show everything
        setPrevShowOrphans(showOrphans);
        setPrevSelectedQueueIds(selectedQueueIds);
        setShowOrphans(true);
        setSelectedQueueIds((user.queues || []).map(q => q.id));
      } else {
        // Turning Todos OFF: restore previous state
        if (prevShowOrphans !== null) {
          setShowOrphans(prevShowOrphans);
          setPrevShowOrphans(null);
        }
        if (prevSelectedQueueIds !== null) {
          setSelectedQueueIds(prevSelectedQueueIds);
          setPrevSelectedQueueIds(null);
        }
      }
      return next;
    });
  };

  // feat_344 — "Todos" del filtro de usuarios. Mismo contrato que
  // handleToggleShowAll: ON memoriza el estado y tilda todo; OFF restaura lo
  // memorizado. Sin memoria (p. ej. "Todos" ya venía del localStorage) cae al
  // default del feature (= el propio admin), nunca a vacío: el filtro de
  // usuarios no admite selección vacía.
  const handleToggleAllUsers = () => {
    const allSelected =
      includeUnassigned &&
      availableUsers.length > 0 &&
      availableUsers.every(u => filterUserIds.includes(u.id));
    if (allSelected) {
      const restoredIds = prevFilterUserIds ?? [];
      const restoredUnassigned = prevIncludeUnassigned ?? false;
      const isEmpty = restoredIds.length === 0 && !restoredUnassigned;
      setFilterUserIds(isEmpty ? [user.id] : restoredIds);
      setIncludeUnassigned(isEmpty ? false : restoredUnassigned);
      setPrevFilterUserIds(null);
      setPrevIncludeUnassigned(null);
    } else {
      setPrevFilterUserIds(filterUserIds);
      setPrevIncludeUnassigned(includeUnassigned);
      setFilterUserIds(availableUsers.map(u => u.id));
      setIncludeUnassigned(true);
    }
  };

  const handleChangeTab = (e, newValue) => {
    // Save current ticket for the tab we are LEAVING
    const leavingKey = activeTabKey();
    if (ticketId && leavingKey !== "search" && leavingKey !== "interno") {
      localStorage.setItem(`lastTicket_${leavingKey}`, ticketId);
    }

    // Save current internal chat user when leaving INTERNOS tab
    if (tab === "interno" && internalChatUserRef?.current) {
      localStorage.setItem("lastInternalUser", JSON.stringify(internalChatUserRef.current));
    }

    setTab(newValue);

    // Restore last ticket for the tab we are ENTERING
    const enteringKey = newValue === "open" ? tabOpen : newValue;
    const savedTicket =
      enteringKey !== "search" && enteringKey !== "interno"
        ? localStorage.getItem(`lastTicket_${enteringKey}`)
        : null;

    if (savedTicket) {
      history.push(`/tickets/${savedTicket}`);
    } else {
      history.push("/tickets");
    }

    if (newValue === "interno") {
      setInternalUnread(0);
      // Restore last internal chat user when re-entering INTERNOS
      const savedUser = localStorage.getItem("lastInternalUser");
      if (savedUser) {
        try {
          onSelectInternalUser(JSON.parse(savedUser));
        } catch (e) {
          localStorage.removeItem("lastInternalUser");
        }
      }
    } else {
      onSelectInternalUser(null);
    }
  };

  const handleChangeTabOpen = (e, newValue) => {
    // Save current ticket for the sub-tab we are LEAVING (only within tab=="open")
    if (ticketId) {
      localStorage.setItem(`lastTicket_${tabOpen}`, ticketId);
    }

    setTabOpen(newValue);

    if (newValue === "pending") {
      history.push("/tickets"); // EN ESPERA always starts blank
    } else {
      const savedTicket = localStorage.getItem(`lastTicket_${newValue}`);
      if (savedTicket) {
        history.push(`/tickets/${savedTicket}`);
      } else {
        history.push("/tickets");
      }
    }
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  // feat_142: en compact el label del tab pasa a ser SOLO ícono (+ badges) con tooltip
  // del título completo. En ≥1280px se mantiene el texto como hasta ahora.
  const renderTabLabel = (title, iconEl, badges = null, pulsing = false) => {
    if (!isCompact) {
      return (
        <span
          style={{ display: "flex", alignItems: "center", gap: 3 }}
          className={pulsing ? classes.pulsing : ""}
        >
          {title}
          {badges}
        </span>
      );
    }
    return (
      <Tooltip title={title} arrow>
        <span
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}
          className={pulsing ? classes.pulsing : ""}
        >
          {iconEl}
          {badges}
        </span>
      </Tooltip>
    );
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={handleCloseNewTicketModal}
        initialContact={newTicketInitialContact}
      />
      {selectionMode && (
        <BatchActionBar
          count={selectedIds.size}
          isAdmin={isAdmin}
          deletedView={isDeletedTab}
          onExit={exitSelection}
          onMarkRead={handleBatchMarkRead}
          onResolve={() => {
            // feat_343: con la llave "Resolver directo" el lote cierra sin preguntar,
            // igual que el botón del chat. "sin_cambio" es close-only en
            // BatchResolveTicketsService (no resuelve ningún targetSystemId).
            if (getSetting("directResolve") === "true") {
              handleBatchResolve("sin_cambio");
              return;
            }
            setResolveDialogOpen(true);
          }}
          onTransfer={() => setBatchTransferOpen(true)}
          onDelete={() => setBatchDeleteOpen(true)}
          onRestore={handleBatchRestore}
          onHardDelete={handleBatchHardDelete}
          onSelectAllVisible={selectAllVisible}
        />
      )}
      <ResolveTicketDialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        count={selectedIds.size}
        loading={batchLoading}
        onResolve={handleBatchResolve}
      />
      <BatchTransferDialog
        open={batchTransferOpen}
        onClose={() => setBatchTransferOpen(false)}
        count={selectedIds.size}
        loading={batchLoading}
        onTransfer={handleBatchTransfer}
      />
      <BatchConfirmDeleteDialog
        open={batchDeleteOpen}
        onClose={() => setBatchDeleteOpen(false)}
        count={selectedIds.size}
        loading={batchLoading}
        onConfirm={handleBatchDelete}
      />
      <Dialog
        open={hardDeleteConfirmOpen}
        onClose={() => { setHardDeleteConfirmOpen(false); setHardDeleteSingleId(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ color: "#f44336" }}>
          {i18n.t("batchHardDeleteDialog.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {hardDeleteSingleId !== null
              ? i18n.t("batchHardDeleteDialog.bodySingle")
              : i18n.t("batchHardDeleteDialog.body", { count: selectedIds.size })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHardDeleteConfirmOpen(false); setHardDeleteSingleId(null); }}>
            {i18n.t("batchHardDeleteDialog.cancel")}
          </Button>
          <Button
            onClick={confirmHardDelete}
            variant="contained"
            style={{ backgroundColor: "#f44336", color: "#fff" }}
            disabled={batchLoading}
          >
            {i18n.t("batchHardDeleteDialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <Paper elevation={0} square className={classes.tabsHeader} style={{ display: "flex", alignItems: "stretch" }}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          aria-label="icon label tabs example"
          style={{ minHeight: 52, flex: 1 }}
        >
          <Tab
            value={"open"}
            icon={isCompact ? undefined : <MoveToInboxIcon style={{ fontSize: 18 }} />}
            label={renderTabLabel(
              i18n.t("tickets.tabs.open.title"),
              <MoveToInboxIcon style={{ fontSize: 20 }} />,
              <>
                {/* feat_323: los dos badges llevan tooltip explicando qué cuenta
                    cada color. En unificado es la única pista de qué significan
                    el azul y el ámbar (ya no hay sub-tabs que los nombren). */}
                {displayOpenCount > 0 && (
                  <Tooltip title={i18n.t(openBadgeTooltip)} arrow>
                    <span style={{ background: "#1976d2", color: "#fff", borderRadius: 8, padding: "0 5px", fontSize: 9, fontWeight: 700, lineHeight: "14px" }}>
                      {displayOpenCount}
                    </span>
                  </Tooltip>
                )}
                {displayPendingCount > 0 && (
                  <Tooltip title={i18n.t(pendingBadgeTooltip)} arrow>
                    <span style={{ background: "#f57c00", color: "#fff", borderRadius: 8, padding: "0 5px", fontSize: 9, fontWeight: 700, lineHeight: "14px" }}>
                      {displayPendingCount}
                    </span>
                  </Tooltip>
                )}
                {unansweredFilterIcon}
              </>
            )}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"closed"}
            icon={isCompact ? undefined : <CheckBoxIcon style={{ fontSize: 18 }} />}
            label={renderTabLabel(
              i18n.t("tickets.tabs.closed.title"),
              <CheckBoxIcon style={{ fontSize: 20 }} />
            )}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"deleted"}
            icon={isCompact ? undefined : <DeleteOutlineIcon style={{ fontSize: 18 }} />}
            label={renderTabLabel(
              i18n.t("tickets.tabs.deleted.title"),
              <DeleteOutlineIcon style={{ fontSize: 20 }} />
            )}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"search"}
            icon={isCompact ? undefined : <SearchIcon style={{ fontSize: 18 }} />}
            label={renderTabLabel(
              i18n.t("tickets.tabs.search.title"),
              <SearchIcon style={{ fontSize: 20 }} />
            )}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"interno"}
            icon={isCompact ? undefined : <PeopleIcon style={{ fontSize: 18 }} />}
            label={renderTabLabel(
              "Interno",
              <PeopleIcon style={{ fontSize: 20 }} />,
              internalUnread > 0 ? (
                <span style={{ background: "#f44336", color: "#fff", borderRadius: 8, padding: "0 5px", fontSize: 9, fontWeight: 700, lineHeight: "14px" }}>
                  {internalUnread}
                </span>
              ) : null,
              internalUnread > 0
            )}
            classes={{ root: classes.tab }}
          />
        </Tabs>
        {/* feat_143: botón NUEVO movido aquí — afuera del <Tabs> para no romper el
            reparto fullWidth de los 5 tabs. Libera el ancho de la fila de filtros. */}
        <Button
          variant="outlined"
          color="primary"
          size="small"
          style={{
            fontSize: isCompact ? 10 : 11,
            padding: isCompact ? "2px 6px" : "2px 8px",
            minWidth: isCompact ? 50 : "auto",
            alignSelf: "center",
            marginRight: 8,
            marginLeft: 4,
            flexShrink: 0,
          }}
          onClick={() => setNewTicketModalOpen(true)}
        >
          {i18n.t("ticketsManager.buttons.newTicket")}
        </Button>
      </Paper>
      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        {tab !== "search" && (
          <Typography variant="body2" className={classes.filtersLabel}>
            {i18n.t("ticketsList.filters.filtersLabel")}
          </Typography>
        )}
        {tab === "search" ? (
          <div className={classes.serachInputWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
          </div>
        ) : (
          <>
            {/* feat_143: botón NUEVO movido al header de tabs (afuera de esta fila). */}
            {/* feat_142: en compact los 3 selects se dejan encoger desde afuera (no aceptan
                prop compact; el wrapper limita su ancho sin tocar los componentes hijo). */}
            <div style={isCompact ? { minWidth: 0, flexShrink: 1, maxWidth: 110 } : undefined}>
              <TicketsTagSelect
                selectedTagIds={selectedTagIds}
                userTags={availableTags}
                onChange={setSelectedTagIds}
              />
            </div>
            <div style={isCompact ? { minWidth: 0, flexShrink: 1, maxWidth: 110 } : undefined}>
              <TicketsContactTypeSelect
                selectedContactTypeIds={selectedContactTypeIds}
                userContactTypes={availableContactTypes}
                onChange={setSelectedContactTypeIds}
              />
            </div>
          </>
        )}
        {/* Filtro Estado — antes de Departamento (single-select) */}
        {availableStates.length > 0 && (() => {
          const activeState = contactStateFilter ? availableStates.find(s => s.id === contactStateFilter) : null;
          return activeState ? (
            <Chip
              size="small"
              label={activeState.name}
              onDelete={() => setContactStateFilter("")}
              style={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                backgroundColor: activeState.color + "28",
                border: `1px solid ${activeState.color}`,
                color: "#333",
              }}
            />
          ) : (
            <Select
              value={contactStateFilter}
              onChange={e => setContactStateFilter(e.target.value)}
              displayEmpty
              disableUnderline
              style={{
                fontSize: isCompact ? 10 : 11,
                height: 22,
                minWidth: isCompact ? 80 : 120,
                maxWidth: isCompact ? 140 : 190,
                border: "1px solid rgba(0,0,0,0.18)",
                borderRadius: 10,
                padding: "0 4px",
                background: "transparent",
                opacity: 0.7,
              }}
              inputProps={{ style: { padding: "0 4px", fontSize: isCompact ? 10 : 11 } }}
            >
              <MenuItem value="" style={{ fontSize: 11 }}>
                <em>{i18n.t("ticketsList.filters.statePlaceholder")}</em>
              </MenuItem>
              {availableStates.map(s => (
                <MenuItem key={s.id} value={s.id} style={{ fontSize: 11 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: s.color, flexShrink: 0 }} />
                    {s.name}
                  </span>
                </MenuItem>
              ))}
            </Select>
          );
        })()}
        {/* feat_301: filtro por usuario asignado — solo admin + setting ON */}
        {userFilterActive && (
          <div style={isCompact ? { minWidth: 0, flexShrink: 1, maxWidth: 120 } : undefined}>
            <TicketsUserSelect
              users={availableUsers}
              selectedUserIds={filterUserIds}
              includeUnassigned={includeUnassigned}
              onChangeUsers={setFilterUserIds}
              onChangeUnassigned={setIncludeUnassigned}
              onToggleAllUsers={handleToggleAllUsers}
            />
          </div>
        )}
        {/* feat_326: selector de orden — solo en modo smart */}
        {isSmartInbox && (
          <Select
            native
            disableUnderline
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value)}
            style={{
              fontSize: isCompact ? 10 : 11,
              height: 22,
              border: "1px solid rgba(0,0,0,0.18)",
              borderRadius: 10,
              padding: "0 6px",
              opacity: 0.7,
              // Piso, no ancho fijo: el <select> nativo crece con el texto de su
              // option más largo, así que bajarlo no corta "Urgentes primero".
              minWidth: isCompact ? 100 : 110,
            }}
          >
            <option value="chrono">
              {i18n.t("settingsExtra.inboxSortChrono")}
            </option>
            <option value="urgent">
              {i18n.t("settingsExtra.inboxSortUrgent")}
            </option>
          </Select>
        )}
        {/* feat_326: toggle "solo sin responder". Es un FILTRO (recorta la
            lista), así que activo se muestra como chip coloreado con X — el
            usuario tiene que ver por qué está viendo menos tickets. */}
        {isSmartInbox && (
          onlyUnanswered ? (
            <Tooltip title={i18n.t("settingsExtra.inboxUnansweredTooltip")} arrow>
              <Chip
                size="small"
                label={i18n.t("settingsExtra.inboxUnansweredActive")}
                onDelete={() => setOnlyUnanswered(false)}
                style={{
                  backgroundColor: "#e6510022",
                  border: "1px solid #e65100",
                  color: "#e65100",
                  fontWeight: 600,
                  fontSize: 10,
                  height: 22,
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title={i18n.t("settingsExtra.inboxUnansweredTooltip")} arrow>
              <Chip
                size="small"
                label={i18n.t("settingsExtra.inboxUnansweredLabel")}
                onClick={() => setOnlyUnanswered(true)}
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid rgba(0,0,0,0.18)",
                  color: "rgba(0,0,0,0.54)",
                  fontSize: 10,
                  height: 22,
                  cursor: "pointer",
                  opacity: 0.7,
                }}
              />
            </Tooltip>
          )
        )}
        <div style={isCompact ? { minWidth: 0, flexShrink: 1 } : undefined}>
          <TicketsQueueSelect
            selectedQueueIds={selectedQueueIds}
            userQueues={user?.queues}
            onChange={(newIds) => setSelectedQueueIds(newIds)}
            showAllTickets={showAllTickets}
            showOrphans={showOrphans}
            canSeeUnassigned={!!user?.canSeeUnassigned}
            isAdmin={user?.profile === "admin"}
            onToggleShowAll={handleToggleShowAll}
            onToggleShowOrphans={() => setShowOrphans(prev => !prev)}
          />
        </div>
      </Paper>
      {/* Toggles Grupos/Canales movidos al menú de perfil (header) — viven en UiPrefsContext. */}
      {/* Panel unificado cuando hay filtro de estado activo — muestra todos los status */}
      {contactStateFilter && (
        <div style={{ flex: 1, flexDirection: "column", overflow: "hidden", display: tab !== "search" && tab !== "interno" && tab !== "deleted" ? "flex" : "none" }}>
          <TicketsList
            showAll={showAllTickets}
            showOrphans={showOrphans}
            selectedQueueIds={selectedQueueIds}
            hideChannels={hideChannels}
            showGroups={showGroups}
            onAcceptTicket={handleAcceptTicket}
            contactStateId={contactStateFilter}
            tagIds={selectedTagIds}
            contactTypeIds={selectedContactTypeIds}
            filterUserIds={effectiveFilterUserIds}
            includeUnassigned={effectiveIncludeUnassigned}
            showStatusBadge={true}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onLongPress={enterSelection}
            onEnterSelection={enterSelection}
            onVisibleTicketsChange={handleVisibleTicketsChange}
          />
        </div>
      )}
      {/* Always mounted — display:none preserves socket state and ticket list */}
      <div style={{ display: !contactStateFilter && tab === "open" ? "flex" : "none", flex: 1, flexDirection: "column", overflow: "hidden" }}>
        {/* feat_323: en unificado no hay sub-tabs — la partición EN ATENCIÓN /
            EN ESPERA deja de existir como navegación y pasa a ser el chip por
            fila (showStatusBadge) + los dos badges del tab de arriba. */}
        {!isUnifiedInbox && (
        <Tabs
          value={tabOpen}
          onChange={handleChangeTabOpen}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          style={{ minHeight: 36 }}
        >
          <Tab
            label={
              <Tooltip title={isSmartInbox ? i18n.t("settingsExtra.inboxSmartAttendingTooltip") : ""} arrow>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {isCompact ? i18n.t("ticketsList.assignedHeaderShort") : i18n.t("ticketsList.assignedHeader")}
                  <span style={{ color: tabOpen === "open" ? "#1976d2" : "#999", fontWeight: 700 }}>
                    {isSmartInbox ? smartAttendingCount : openCount}
                  </span>
                  {unansweredFilterIcon}
                </span>
              </Tooltip>
            }
            value={"open"}
            style={{ minHeight: isCompact ? 32 : 36, fontSize: isCompact ? 10 : 11 }}
          />
          <Tab
            label={
              <Tooltip title={isSmartInbox ? i18n.t("settingsExtra.inboxSmartWaitingTooltip") : ""} arrow>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {isCompact ? i18n.t("ticketsList.pendingHeaderShort") : i18n.t("ticketsList.pendingHeader")}
                  {/* feat_325: en smart el contador va ámbar SIEMPRE (no solo al
                      estar activo) — son los que nadie ruteó todavía y el punto
                      del modo es que se noten aunque estés parado en el otro tab. */}
                  <span style={{
                    color: isSmartInbox ? "#f57c00" : (tabOpen === "pending" ? "#1976d2" : "#999"),
                    fontWeight: 700,
                  }}>
                    {pendingCount}
                  </span>
                  {unansweredFilterIcon}
                </span>
              </Tooltip>
            }
            value={"pending"}
            style={{ minHeight: isCompact ? 32 : 36, fontSize: isCompact ? 10 : 11 }}
          />
        </Tabs>
        )}
        <Paper className={classes.ticketsWrapper}>
          {isSmartInbox ? (
          /* MODO SMART — los dos sub-tabs vuelven, pero el corte NO es
             open vs pending: es TIENE DESTINO vs HUÉRFANO.
               EN ATENCIÓN = open + pending con cola  (assigned_or_open)
               EN ESPERA   = pending sin cola         (unassigned)
             Las dos listas PARTICIONAN el conjunto activo: ningún ticket queda
             fuera de las dos. Siguen montadas a la vez (applyPanelStyle las
             oculta) para no perder el estado del socket, igual que en split. */
          <>
          <TicketsList
            key="smart-attending"
            status={SMART_ATTENDING_STATUSES}
            queueAssignment="assigned_or_open"
            sortOrder={sortOrder}
            onlyUnanswered={onlyUnanswered}
            showAll={showAllTickets}
            showOrphans={showOrphans}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val, byStatus) => {
              setUnifiedOpenCount(byStatus?.open ?? 0);
              setUnifiedPendingCount(byStatus?.pending ?? 0);
            }}
            style={applyPanelStyle("open")}
            hideChannels={hideChannels}
            showGroups={showGroups}
            onAcceptTicket={handleAcceptTicket}
            tagIds={selectedTagIds}
            contactTypeIds={selectedContactTypeIds}
            filterUserIds={effectiveFilterUserIds}
            includeUnassigned={effectiveIncludeUnassigned}
            showStatusBadge={true}
            selectionMode={selectionMode && tabOpen === "open"}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onLongPress={enterSelection}
            onEnterSelection={enterSelection}
            onVisibleTicketsChange={tabOpen === "open" ? handleVisibleTicketsChange : undefined}
          />
          {/* ⚠ EN ESPERA fuerza showOrphans y NO aplica el filtro por asesor:
              un huérfano no tiene cola ni dueño, así que recortarlo por cola o
              por usuario lo dejaría siempre vacío. El scope de rol del backend
              sigue mandando (un non-admin sin canSeeUnassigned no ve ninguno) y
              el companyId sale del JWT, nunca del request. */}
          <TicketsList
            key="smart-waiting"
            status={SMART_WAITING_STATUSES}
            queueAssignment="unassigned"
            sortOrder={sortOrder}
            onlyUnanswered={onlyUnanswered}
            showAll={showAllTickets}
            showOrphans={true}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
            hideChannels={hideChannels}
            showGroups={showGroups}
            onAcceptTicket={handleAcceptTicket}
            tagIds={selectedTagIds}
            contactTypeIds={selectedContactTypeIds}
            filterUserIds={[]}
            includeUnassigned={true}
            showStatusBadge={false}
            selectionMode={selectionMode && tabOpen === "pending"}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onLongPress={enterSelection}
            onEnterSelection={enterSelection}
            onVisibleTicketsChange={tabOpen === "pending" ? handleVisibleTicketsChange : undefined}
          />
          </>
          ) : isUnifiedInbox ? (
          /* MODO UNIFICADO — una sola lista con open+pending, ordenada por
             updatedAt DESC (el orden por default del servicio). El chip por fila
             (showStatusBadge) es lo que distingue uno de otro. */
          <TicketsList
            key="unified-inbox"
            status={UNIFIED_STATUSES}
            showAll={showAllTickets}
            showOrphans={showOrphans}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val, byStatus) => {
              setUnifiedOpenCount(byStatus?.open ?? 0);
              setUnifiedPendingCount(byStatus?.pending ?? 0);
            }}
            hideChannels={hideChannels}
            showGroups={showGroups}
            onAcceptTicket={handleAcceptTicket}
            tagIds={selectedTagIds}
            contactTypeIds={selectedContactTypeIds}
            filterUserIds={effectiveFilterUserIds}
            includeUnassigned={effectiveIncludeUnassigned}
            showStatusBadge={true}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onLongPress={enterSelection}
            onEnterSelection={enterSelection}
            onVisibleTicketsChange={handleVisibleTicketsChange}
          />
          ) : (
          <>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            showOrphans={showOrphans}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
            hideChannels={hideChannels}
            showGroups={showGroups}
            tagIds={selectedTagIds}
            contactTypeIds={selectedContactTypeIds}
            filterUserIds={effectiveFilterUserIds}
            includeUnassigned={effectiveIncludeUnassigned}
            selectionMode={selectionMode && tabOpen === "open"}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onLongPress={enterSelection}
            onEnterSelection={enterSelection}
            onVisibleTicketsChange={tabOpen === "open" ? handleVisibleTicketsChange : undefined}
          />
          <TicketsList
            status="pending"
            showAll={showAllTickets}
            showOrphans={showOrphans}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
            hideChannels={hideChannels}
            showGroups={showGroups}
            onAcceptTicket={handleAcceptTicket}
            tagIds={selectedTagIds}
            contactTypeIds={selectedContactTypeIds}
            filterUserIds={effectiveFilterUserIds}
            includeUnassigned={effectiveIncludeUnassigned}
            selectionMode={selectionMode && tabOpen === "pending"}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onLongPress={enterSelection}
            onEnterSelection={enterSelection}
            onVisibleTicketsChange={tabOpen === "pending" ? handleVisibleTicketsChange : undefined}
          />
          </>
          )}
        </Paper>
      </div>
      <div style={{ display: !contactStateFilter && tab === "closed" ? "flex" : "none", flex: 1, flexDirection: "column", overflow: "hidden" }}>
        <TicketsList
          status="closed"
          showAll={showAllTickets}
          showOrphans={showOrphans}
          selectedQueueIds={selectedQueueIds}
          hideChannels={hideChannels}
          showGroups={showGroups}
          tagIds={selectedTagIds}
          contactTypeIds={selectedContactTypeIds}
          filterUserIds={effectiveFilterUserIds}
          includeUnassigned={effectiveIncludeUnassigned}
          selectionMode={selectionMode && tab === "closed"}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onLongPress={enterSelection}
          onEnterSelection={enterSelection}
          onVisibleTicketsChange={tab === "closed" ? handleVisibleTicketsChange : undefined}
        />
      </div>
      <div style={{ display: tab === "deleted" ? "flex" : "none", flex: 1, flexDirection: "column", overflow: "hidden" }}>
        <TicketsList
          key={`deleted-${deletedRefreshKey}`}
          // Sala propia de papelera. `status` sigue undefined (el fetch va con
          // deleted=true); esto solo cambia a qué room de socket se une, para
          // recibir el delete que emite el restore y sacar la fila en vivo.
          socketStatus="deleted"
          showAll={isAdmin ? true : false}
          showOrphans={isAdmin ? true : showOrphans}
          selectedQueueIds={isAdmin ? (user.queues || []).map(q => q.id) : selectedQueueIds}
          hideChannels={false}
          showGroups={true}
          tagIds={[]}
          contactTypeIds={[]}
          deletedView={true}
          showStatusBadge={true}
          onRestoreSingle={handleRestoreSingle}
          onHardDeleteSingle={isAdmin ? handleHardDeleteSingle : undefined}
          isAdmin={isAdmin}
          selectionMode={selectionMode && tab === "deleted"}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onLongPress={enterSelection}
          onEnterSelection={enterSelection}
          onVisibleTicketsChange={tab === "deleted" ? handleVisibleTicketsChange : undefined}
        />
      </div>
      {tab === "search" && (
        <div style={{ flex: 1, flexDirection: "column", overflow: "hidden", display: "flex" }}>
          <TicketsList
            searchParam={debouncedSearchParam}
            showAll={showAllTickets}
            showOrphans={showOrphans}
            selectedQueueIds={selectedQueueIds}
            hideChannels={hideChannels}
            showGroups={showGroups}
            tagIds={selectedTagIds}
            contactTypeIds={selectedContactTypeIds}
            filterUserIds={effectiveFilterUserIds}
            includeUnassigned={effectiveIncludeUnassigned}
            onStartChatWithContact={handleStartChatWithContact}
            selectionMode={selectionMode && tab === "search"}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onLongPress={enterSelection}
            onEnterSelection={enterSelection}
            onVisibleTicketsChange={tab === "search" ? handleVisibleTicketsChange : undefined}
          />
        </div>
      )}
      <div style={{ display: tab === "interno" ? "flex" : "none", flex: 1, flexDirection: "column", overflow: "hidden" }}>
        <InternalChatPanel onSelectUser={onSelectInternalUser} />
      </div>
    </Paper>
  );
};

export default TicketsManager;
